import { CalendarCheck } from "lucide-react";
import { BOOK_CALL_URL, SECTION_LABEL } from "@/lib/site";
import { track } from "@/lib/track";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const MODELS = [
    {
        title: "Fixed-Scope Project",
        desc: "For a defined website, application or automation with agreed deliverables, timeline and price.",
        id: "fixed-scope",
    },
    {
        title: "White-Label Partnership",
        desc: "For agencies that need additional development support delivered under their own brand.",
        id: "white-label",
        featured: true,
    },
    {
        title: "Custom Implementation",
        desc: "For more complex workflows, systems and integrations shaped around your operations.",
        id: "custom",
    },
];

export default function Engagement() {
    const externalCall = BOOK_CALL_URL.startsWith("http");

    return (
        <section
            id="engagement"
            className="scroll-mt-20 bg-[#F7F0EA]"
            data-testid="engagement-section"
        >
            <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
                <Reveal>
                    <p className={SECTION_LABEL}>Working Together</p>
                    <h2 className="mt-4 max-w-2xl font-heading text-3xl font-medium tracking-tight text-[#1C1917] md:text-4xl lg:text-[3.25rem] lg:leading-[1.1]">
                        Three clear ways to{" "}
                        <span className="font-serif italic text-[#CF4242]">engage.</span>
                    </h2>
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#57504A] md:text-lg">
                        Pricing depends on scope, integrations, complexity and timeline. Once
                        we understand your requirements, you receive a clearly defined quote
                        before any work begins.
                    </p>
                </Reveal>

                <Stagger className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
                    {MODELS.map((m) => (
                        <StaggerItem key={m.id} className="h-full">
                            <article
                                data-testid={`engagement-card-${m.id}`}
                                className={`spotlight group relative flex h-full flex-col overflow-hidden rounded-[1.25rem] bg-white p-8 transition-[transform,box-shadow] duration-500 ease-out hover:-translate-y-2 hover:shadow-[0_32px_60px_-24px_rgb(28,25,23,0.35)] ${
                                    m.featured
                                        ? "border border-[#CF4242]/40 shadow-[0_20px_50px_-24px_rgb(207,66,66,0.55)] ring-1 ring-[#CF4242]/20 md:-mt-4 md:pb-12"
                                        : "border border-[#EDE3DA] shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                                }`}
                            >
                                <span
                                    className="absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r from-[#CF4242] via-[#E8764A] to-[#CF4242] transition-transform duration-500 group-hover:scale-x-100"
                                    aria-hidden="true"
                                />
                                {m.featured && (
                                    <span className="absolute right-6 top-6 rounded-full bg-[#FBECE9] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#A92F31]">
                                        For Agencies
                                    </span>
                                )}
                                <h3
                                    className={`relative font-heading text-xl font-semibold text-[#1C1917] ${
                                        m.featured ? "pr-24" : ""
                                    }`}
                                >
                                    {m.title}
                                </h3>
                                <p className="relative mt-3 flex-1 text-sm leading-relaxed text-[#57504A]">
                                    {m.desc}
                                </p>
                                <a
                                    href={BOOK_CALL_URL}
                                    target={externalCall ? "_blank" : undefined}
                                    rel={externalCall ? "noopener noreferrer" : undefined}
                                    data-testid={`engagement-${m.id}-call-btn`}
                                    onClick={() => track("cta_click", { cta: `book_call_${m.id}` })}
                                    className="btn-primary btn-sheen relative mt-7 inline-flex items-center gap-1.5 self-start overflow-hidden rounded-full bg-[#CF4242] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_-10px_rgb(207,66,66,0.8)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#A92F31] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#CF4242]"
                                >
                                    <CalendarCheck size={15} aria-hidden="true" /> Book a Free
                                    Consultancy Call
                                </a>
                            </article>
                        </StaggerItem>
                    ))}
                </Stagger>
            </div>
        </section>
    );
}
